import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrollY, setScrollY] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const [stats, setStats] = useState({
    artists: 0,
    domestic: 0,
    year: 0
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
          
          // Stats 섹션 감지
          if (entry.target.id === 'stats-section' && !statsVisible) {
            setStatsVisible(true);
          }
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll, .animate-slide-left, .animate-slide-right');
    animatedElements.forEach((el) => observer.observe(el));

    // Stats 섹션 관찰
    const statsSection = document.getElementById('stats-section');
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => observer.disconnect();
  }, [statsVisible]);

  // 숫자 카운트업 애니메이션
  useEffect(() => {
    if (!statsVisible) return;

    const duration = 2000; // 2초
    const steps = 60;
    const stepDuration = duration / steps;

    const targets = {
      artists: 600,
      domestic: 100,
      year: 2023
    };

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setStats({
        artists: Math.floor(targets.artists * progress),
        domestic: Math.floor(targets.domestic * progress),
        year: Math.floor(2023 + (targets.year - 2023) * progress)
      });

      if (currentStep >= steps) {
        setStats(targets);
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [statsVisible]);

  return (
    <div className="min-h-screen bg-white snap-y snap-mandatory overflow-y-scroll h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen snap-start snap-always flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        {/* Animated Background Orbs */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute top-20 left-10 w-72 h-72 bg-cyan-400 rounded-full blur-3xl"
            style={{ 
              transform: `translateY(${scrollY * 0.3}px)`,
              animation: 'float 6s ease-in-out infinite, pulse 4s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="absolute bottom-20 right-10 w-96 h-96 bg-teal-400 rounded-full blur-3xl"
            style={{ 
              transform: `translateY(${-scrollY * 0.2}px)`,
              animation: 'float 8s ease-in-out infinite reverse, pulse 5s ease-in-out infinite'
            }}
          ></div>
        </div>

        {/* Flowing Wave Ribbons - Centered and Diagonal, touching bottom */}
        <div className="absolute inset-0 flex items-end justify-center overflow-hidden pointer-events-none">
          {/* Wave 1 - Cyan to Teal gradient */}
          <svg 
            className="absolute w-[120%] h-[100%] opacity-60"
            style={{ 
              transform: `rotate(15deg)`,
              transformOrigin: 'center bottom',
              bottom: 0
            }}
            viewBox="0 0 1200 600" 
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#06b6d4', stopOpacity: 0.3 }} />
                <stop offset="50%" style={{ stopColor: '#14b8a6', stopOpacity: 0.5 }} />
                <stop offset="100%" style={{ stopColor: '#22d3ee', stopOpacity: 0.3 }} />
              </linearGradient>
            </defs>
            <path 
              d="M0,300 Q300,200 600,300 T1200,300 L1200,600 L0,600 Z" 
              fill="url(#gradient1)"
              style={{ animation: 'wave1 8s ease-in-out infinite' }}
            />
            <path 
              d="M0,300 Q300,200 600,300 T1200,300" 
              fill="none"
              stroke="url(#gradient1)"
              strokeWidth="2"
              style={{ animation: 'wave1 8s ease-in-out infinite' }}
            />
          </svg>

          {/* Wave 2 - Teal to Cyan gradient */}
          <svg 
            className="absolute w-[120%] h-[100%] opacity-50"
            style={{ 
              transform: `rotate(-10deg)`,
              transformOrigin: 'center bottom',
              bottom: 0
            }}
            viewBox="0 0 1200 600" 
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#22d3ee', stopOpacity: 0.3 }} />
                <stop offset="50%" style={{ stopColor: '#14b8a6', stopOpacity: 0.6 }} />
                <stop offset="100%" style={{ stopColor: '#06b6d4', stopOpacity: 0.3 }} />
              </linearGradient>
            </defs>
            <path 
              d="M0,250 Q300,350 600,250 T1200,250 L1200,600 L0,600 Z" 
              fill="url(#gradient2)"
              style={{ animation: 'wave2 10s ease-in-out infinite' }}
            />
            <path 
              d="M0,250 Q300,350 600,250 T1200,250" 
              fill="none"
              stroke="url(#gradient2)"
              strokeWidth="2"
              style={{ animation: 'wave2 10s ease-in-out infinite' }}
            />
          </svg>

          {/* Wave 3 - Cyan gradient */}
          <svg 
            className="absolute w-[120%] h-[100%] opacity-40"
            style={{ 
              transform: `rotate(5deg)`,
              transformOrigin: 'center bottom',
              bottom: 0
            }}
            viewBox="0 0 1200 600" 
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#0891b2', stopOpacity: 0.3 }} />
                <stop offset="50%" style={{ stopColor: '#06b6d4', stopOpacity: 0.5 }} />
                <stop offset="100%" style={{ stopColor: '#22d3ee', stopOpacity: 0.3 }} />
              </linearGradient>
            </defs>
            <path 
              d="M0,350 Q300,250 600,350 T1200,350 L1200,600 L0,600 Z" 
              fill="url(#gradient3)"
              style={{ animation: 'wave3 12s ease-in-out infinite' }}
            />
            <path 
              d="M0,350 Q300,250 600,350 T1200,350" 
              fill="none"
              stroke="url(#gradient3)"
              strokeWidth="2"
              style={{ animation: 'wave3 12s ease-in-out infinite' }}
            />
          </svg>
        </div>

        {/* Floating Particles around text */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Top left area */}
          <div 
            className="absolute top-[35%] left-[15%] w-3 h-3 bg-cyan-400 rounded-full opacity-60"
            style={{ 
              animation: 'bounce 3s ease-in-out infinite, glow 2s ease-in-out infinite alternate'
            }}
          ></div>
          <div 
            className="absolute top-[42%] left-[12%] w-2 h-2 bg-teal-300 rounded-full opacity-50"
            style={{ 
              animation: 'float 4.5s ease-in-out infinite, glow 2.5s ease-in-out infinite alternate'
            }}
          ></div>
          <div 
            className="absolute top-[28%] left-[20%] w-1 h-1 bg-cyan-200 rounded-full opacity-40"
            style={{ 
              animation: 'twinkle 2s ease-in-out infinite'
            }}
          ></div>

          {/* Top right area */}
          <div 
            className="absolute top-[32%] right-[18%] w-2 h-2 bg-cyan-300 rounded-full opacity-50"
            style={{ 
              animation: 'float 5s ease-in-out infinite reverse, glow 3s ease-in-out infinite alternate'
            }}
          ></div>
          <div 
            className="absolute top-[38%] right-[14%] w-3 h-3 bg-teal-400 rounded-full opacity-40"
            style={{ 
              animation: 'bounce 4s ease-in-out infinite, glow 2.2s ease-in-out infinite alternate'
            }}
          ></div>
          <div 
            className="absolute top-[45%] right-[22%] w-1 h-1 bg-cyan-200 rounded-full opacity-35"
            style={{ 
              animation: 'twinkle 1.8s ease-in-out infinite alternate'
            }}
          ></div>

          {/* Bottom left area */}
          <div 
            className="absolute bottom-[35%] left-[17%] w-2 h-2 bg-teal-300 rounded-full opacity-45"
            style={{ 
              animation: 'float 3.5s ease-in-out infinite, glow 2.8s ease-in-out infinite alternate'
            }}
          ></div>
          <div 
            className="absolute bottom-[40%] left-[25%] w-1 h-1 bg-cyan-400 rounded-full opacity-50"
            style={{ 
              animation: 'twinkle 2.3s ease-in-out infinite'
            }}
          ></div>

          {/* Bottom right area */}
          <div 
            className="absolute bottom-[38%] right-[16%] w-3 h-3 bg-cyan-300 rounded-full opacity-55"
            style={{ 
              animation: 'bounce 5.5s ease-in-out infinite reverse, glow 2.6s ease-in-out infinite alternate'
            }}
          ></div>
          <div 
            className="absolute bottom-[32%] right-[20%] w-2 h-2 bg-teal-400 rounded-full opacity-45"
            style={{ 
              animation: 'float 4.2s ease-in-out infinite, glow 3.2s ease-in-out infinite alternate'
            }}
          ></div>
          <div 
            className="absolute bottom-[45%] right-[12%] w-1 h-1 bg-cyan-200 rounded-full opacity-40"
            style={{ 
              animation: 'twinkle 1.5s ease-in-out infinite alternate'
            }}
          ></div>

          {/* Middle scattered particles */}
          <div 
            className="absolute top-[50%] left-[28%] w-1 h-1 bg-teal-300 rounded-full opacity-35"
            style={{ 
              animation: 'float 6s ease-in-out infinite, twinkle 2.5s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="absolute top-[55%] right-[30%] w-2 h-2 bg-cyan-400 rounded-full opacity-40"
            style={{ 
              animation: 'bounce 4.8s ease-in-out infinite, glow 2.4s ease-in-out infinite alternate'
            }}
          ></div>
        </div>
        
        <div 
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32 text-center z-10"
          style={{ transform: `translateY(${scrollY * 0.15}px)` }}
        >
          {/* Logo */}
          <div className="mb-12 sm:mb-16">
            <img 
              alt="RAON TECH" 
              className="h-12 sm:h-16 lg:h-20 w-auto object-contain mx-auto" 
              src="https://static.readdy.ai/image/71503c3d7482a205559d38205fea6d8a/667be669e17d2fbd92f9c764dec37c73.png"
            />
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 sm:mb-8 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span className="block text-white font-serif italic" style={{ textShadow: '0 4px 20px rgba(6, 182, 212, 0.5), 0 2px 10px rgba(20, 184, 166, 0.3)' }}>Game & Webtoon</span>
            <span className="block text-white mt-2 sm:mt-4" style={{ textShadow: '0 4px 20px rgba(6, 182, 212, 0.4), 0 2px 10px rgba(20, 184, 166, 0.2)' }}>Source Development</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4" style={{ fontFamily: "'Noto Sans KR', sans-serif", textShadow: '0 2px 10px rgba(0, 0, 0, 0.8)' }}>
            2023년부터 게임 리소스 제작 디자이너와 웹툰 소스 개발 디자이너들과 한길만 걸어온 작가 매칭 글로벌 컴퍼니 빌더
          </p>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 sm:bottom-12 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center gap-2">
          <div className="text-white/60 text-xs sm:text-sm tracking-wider">SCROLL</div>
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-2">
            <div 
              className="w-1.5 h-1.5 bg-white/60 rounded-full"
              style={{ animation: 'scrollDown 2s ease-in-out infinite' }}
            ></div>
          </div>
          <div className="flex flex-col gap-1">
            <i 
              className="ri-arrow-down-line text-white/60 text-xl"
              style={{ animation: 'bounceArrow 2s ease-in-out infinite' }}
            ></i>
          </div>
        </div>

        {/* Custom CSS for animations */}
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=Noto+Sans+KR:wght@300;400;500;700&display=swap');
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          @keyframes bounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
          }
          
          @keyframes glow {
            0% { box-shadow: 0 0 5px currentColor; }
            100% { box-shadow: 0 0 20px currentColor, 0 0 30px currentColor; }
          }
          
          @keyframes twinkle {
            0%, 100% { opacity: 0.2; }
            50% { opacity: 1; }
          }

          @keyframes wave1 {
            0%, 100% { 
              d: path("M0,300 Q300,200 600,300 T1200,300 L1200,600 L0,600 Z");
            }
            50% { 
              d: path("M0,300 Q300,400 600,300 T1200,300 L1200,600 L0,600 Z");
            }
          }

          @keyframes wave2 {
            0%, 100% { 
              d: path("M0,250 Q300,350 600,250 T1200,250 L1200,600 L0,600 Z");
            }
            50% { 
              d: path("M0,250 Q300,150 600,250 T1200,250 L1200,600 L0,600 Z");
            }
          }

          @keyframes wave3 {
            0%, 100% { 
              d: path("M0,350 Q300,250 600,350 T1200,350 L1200,600 L0,600 Z");
            }
            50% { 
              d: path("M0,350 Q300,450 600,350 T1200,350 L1200,600 L0,600 Z");
            }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(50px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes scrollDown {
            0% {
              transform: translateY(0);
              opacity: 0;
            }
            40% {
              opacity: 1;
            }
            80% {
              transform: translateY(20px);
              opacity: 0;
            }
            100% {
              opacity: 0;
            }
          }

          @keyframes bounceArrow {
            0%, 100% {
              transform: translateY(0);
              opacity: 0.6;
            }
            50% {
              transform: translateY(8px);
              opacity: 1;
            }
          }

          .animate-on-scroll,
          .animate-slide-left,
          .animate-slide-right {
            opacity: 0;
          }

          .animate-on-scroll.animate-visible {
            animation: fadeInUp 0.8s ease-out forwards;
          }

          .animate-slide-left.animate-visible {
            animation: slideInLeft 0.8s ease-out forwards;
          }

          .animate-slide-right.animate-visible {
            animation: slideInRight 0.8s ease-out forwards;
          }
        `}</style>
      </section>

      {/* About Section */}
      <section className="relative py-16 sm:py-24 lg:py-32 bg-gray-900 text-white overflow-hidden snap-start snap-always min-h-screen flex items-center">
        {/* Animated Background - 더 잘 보이게 opacity 증가 */}
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute top-10 right-20 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"
            style={{ animation: 'float 10s ease-in-out infinite' }}
          ></div>
          <div 
            className="absolute bottom-10 left-20 w-80 h-80 bg-teal-400 rounded-full blur-3xl"
            style={{ animation: 'float 12s ease-in-out infinite reverse' }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto animate-on-scroll">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 text-white font-serif italic">About Us</h2>
            <div className="space-y-4 sm:space-y-6 text-gray-300 leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
              <p className="text-base sm:text-lg">
                (주) 라온테크는 2023년 게임소싱 개발을 시작하여 오로지 게임&웹툰 소스 개발이라는 한 길만 걸어온 기업입니다.
              </p>
              <p className="text-base sm:text-lg">
                국내에만 <strong className="text-white font-semibold">100명의 베테랑 작가</strong>를 보유하고 있으며 대규모 개발의 성공적인 런칭을 위해 미국, 유럽, 아시아 등 <strong className="text-white font-semibold">글로벌 Full Stack 작가 500명</strong>과 협업하여 가장 안정적이고 최신 기술을 적용합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Builder Section */}
      <section className="relative py-16 sm:py-24 lg:py-32 bg-black overflow-hidden snap-start snap-always min-h-screen flex items-center">
        {/* Animated Background - 더 잘 보이게 opacity 증가 */}
        <div className="absolute inset-0 opacity-15">
          <div 
            className="absolute top-1/4 left-1/4 w-72 h-72 bg-cyan-400 rounded-full blur-3xl"
            style={{ animation: 'float 9s ease-in-out infinite' }}
          ></div>
          <div 
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-400 rounded-full blur-3xl"
            style={{ animation: 'float 11s ease-in-out infinite reverse' }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12 sm:mb-20 animate-on-scroll">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white font-serif italic">Company Builder</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed px-4">
              작가 발굴 및 프로젝트 양도만 잘 하는 기업이 아닌<br className="hidden sm:block" />
              사업 기획부터 운영까지 잘하는 컴퍼니빌더입니다
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-gray-900 p-6 sm:p-8 lg:p-10 rounded-3xl hover:bg-gray-800 transition-all duration-500 border border-gray-800 cursor-pointer hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20 animate-slide-left">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-lightbulb-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-white">사업 기획</h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                시장 분석부터 비즈니스 모델 설계까지, 성공적인 엑시트까지 다양한 경험을 바탕으로 최적의 전략을 제시합니다.
              </p>
            </div>

            <div className="bg-gray-900 p-6 sm:p-8 lg:p-10 rounded-3xl hover:bg-gray-800 transition-all duration-500 border border-gray-800 cursor-pointer hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20 animate-on-scroll" style={{ animationDelay: '0.1s' }}>
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-rocket-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-white">고도 성장</h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                검증된 전략과 글로벌 네트워크로 빠른 성장을 지원합니다.
              </p>
            </div>

            <div className="bg-gray-900 p-6 sm:p-8 lg:p-10 rounded-3xl hover:bg-gray-800 transition-all duration-500 border border-gray-800 cursor-pointer hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20 animate-slide-right">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-trophy-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-white">EXIT 전략</h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                성공적인 엑시트까지 다양한 경험을 바탕으로 최적의 전략을 제시합니다.
              </p>
            </div>
          </div>

          <div className="mt-12 sm:mt-16 text-center px-4 animate-on-scroll" style={{ animationDelay: '0.2s' }}>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed max-w-4xl mx-auto">
              사업 계획부터 고도 성장 그리고 EXIT까지 다양하게 경험한 우리는<br className="hidden sm:block" />
              지금도 새로운 도전을 하고 있습니다.
            </p>
          </div>
        </div>
      </section>

      {/* Game Portfolio Section */}
      <section className="py-16 sm:py-24 lg:py-32 bg-white overflow-hidden snap-start snap-always min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-20 animate-on-scroll">
            <div className="text-[11px] tracking-[0.3em] uppercase text-gray-400 mb-4 sm:mb-6">Game Portfolio</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">
              게임<br />
              <span className="font-serif italic font-normal">프로젝트</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              다양한 장르의 게임 개발 프로젝트를 소개합니다.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Game Portfolio Items */}
            {[
              {
                title: "Eternal Kingdom",
                description: "중세 판타지 배경의 대규모 전략 RPG. 왕국 건설과 영웅 육성 시스템이 핵심입니다.",
                tags: ["Strategy", "RPG", "Fantasy"],
                year: "2024",
                image: "https://public.readdy.ai/ai/img_res/ad599bed3ec9626d72c42aff6254f54d.jpg"
              },
              {
                title: "음양사: 요괴전",
                description: "동양 신화를 바탕으로 한 턴제 RPG. 음양술과 부적을 사용해 요괴를 퇴치하는 전략 게임.",
                tags: ["동양풍", "RPG", "전략"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=oriental%20fantasy%20onmyoji%20game%20traditional%20asian%20mythology%20yokai%20spirits%20paper%20talismans%20mystical%20atmosphere%20professional%20game%20art%20cinematic%20eastern%20aesthetic&width=600&height=400&seq=orientalgame001&orientation=landscape"
              },
              {
                title: "Shadow Hunters",
                description: "어둠 속 몬스터를 사냥하는 협동 액션 게임. 4인 멀티플레이 지원과 다양한 무기 시스템.",
                tags: ["Action", "Co-op", "Horror"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=dark%20monster%20hunting%20game%20team%20of%20hunters%20fighting%20creatures%20atmospheric%20horror%20action%20professional%20game%20screenshot%20dramatic%20lighting&width=600&height=400&seq=newgame003&orientation=landscape"
              },
              {
                title: "한옥마을 귀신이야기",
                description: "조선시대 한옥마을을 배경으로 한 공포 어드벤처. 전통 귀신과 민속 설화가 어우러진 한국형 호러.",
                tags: ["공포", "어드벤처", "한국풍"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=korean%20traditional%20hanok%20village%20horror%20game%20night%20scene%20ghost%20story%20atmospheric%20fear%20traditional%20architecture%20moonlight%20eerie%20professional%20game%20art&width=600&height=400&seq=koreanhorrorgame001&orientation=landscape"
              },
              {
                title: "Ocean Odyssey",
                description: "광활한 바다를 탐험하는 오픈월드 항해 게임. 해적, 보물, 그리고 미지의 섬들.",
                tags: ["Adventure", "Open World", "Exploration"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=pirate%20ship%20sailing%20vast%20ocean%20adventure%20game%20tropical%20islands%20treasure%20hunt%20professional%20game%20screenshot%20beautiful%20seascape%20sunset&width=600&height=400&seq=newgame005&orientation=landscape"
              },
              {
                title: "무협지존",
                description: "중국 무협 세계관의 액션 RPG. 경공과 내공 시스템으로 강호를 누비는 무림 고수의 여정.",
                tags: ["무협", "액션", "RPG"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=chinese%20martial%20arts%20wuxia%20game%20flying%20warriors%20traditional%20temples%20mountain%20peaks%20kung%20fu%20action%20professional%20game%20screenshot%20epic%20oriental%20landscape&width=600&height=400&seq=wuxiagame001&orientation=landscape"
              },
              {
                title: "Asylum: The Forgotten",
                description: "버려진 정신병원을 탐험하는 1인칭 서바이벌 호러. 심리적 공포와 긴장감 넘치는 스토리.",
                tags: ["Horror", "Survival", "Psychological"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=abandoned%20asylum%20horror%20game%20first%20person%20dark%20corridors%20psychological%20terror%20creepy%20atmosphere%20professional%20game%20screenshot%20intense%20fear&width=600&height=400&seq=horrorgame002&orientation=landscape"
              },
              {
                title: "Cyber Revolution",
                description: "사이버펑크 세계관의 오픈월드 액션 RPG. 해킹과 전투를 결합한 미래형 게임플레이.",
                tags: ["사이버펑크", "액션", "RPG"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=cyberpunk%20open%20world%20game%20neon%20city%20futuristic%20hacking%20combat%20professional%20game%20screenshot%20dystopian%20atmosphere%20advanced%20technology&width=600&height=400&seq=cybergame001&orientation=landscape"
              },
              {
                title: "Rhythm Warriors",
                description: "리듬 액션과 전투를 결합한 혁신적인 게임. 음악에 맞춰 적을 물리치는 독특한 시스템.",
                tags: ["Rhythm", "Action", "Music"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=rhythm%20action%20game%20musical%20warriors%20dancing%20fighting%20colorful%20stage%20lights%20professional%20game%20screenshot%20dynamic%20movement%20vibrant%20effects&width=600&height=400&seq=newgame009&orientation=landscape"
              }
            ].map((game, index) => (
              <article key={index} className="group cursor-pointer animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="mb-4 overflow-hidden rounded-2xl bg-gray-100">
                  <img 
                    alt={game.title}
                    className="w-full h-[200px] sm:h-[240px] object-cover transition-transform duration-700 group-hover:scale-110"
                    src={game.image}
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-block px-2.5 py-1 bg-cyan-50 text-cyan-700 text-xs rounded-full">게임</span>
                  <span className="text-xs text-gray-500">{game.year}</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-cyan-600 transition-colors">{game.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">{game.description}</p>
                <div className="flex flex-wrap gap-2">
                  {game.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="px-2.5 py-1 bg-gray-50 text-gray-600 text-xs rounded-full">{tag}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Webtoon Portfolio Section */}
      <section className="py-16 sm:py-24 lg:py-32 bg-gray-50 overflow-hidden snap-start snap-always min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-20 animate-on-scroll">
            <div className="text-[11px] tracking-[0.3em] uppercase text-gray-400 mb-4 sm:mb-6">Webtoon Portfolio</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">
              웹툰<br />
              <span className="font-serif italic font-normal">프로젝트</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              감동과 재미를 선사하는 웹툰 프로젝트를 소개합니다.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Webtoon Portfolio Items */}
            {[
              {
                title: "별빛 아래서",
                description: "천문학자와 카페 주인의 로맨스. 별자리처럼 연결되는 두 사람의 운명적인 만남.",
                tags: ["로맨스", "힐링", "일상"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=romantic%20webtoon%20illustration%20starry%20night%20sky%20couple%20looking%20at%20stars%20cafe%20setting%20warm%20atmosphere%20professional%20korean%20manhwa%20art%20style&width=600&height=400&seq=newwebtoon001&orientation=landscape"
              },
              {
                title: "구미호뎐",
                description: "현대 서울에 나타난 구미호와 인간의 사랑 이야기. 한국 전통 설화를 현대적으로 재해석한 판타지 로맨스.",
                tags: ["판타지", "로맨스", "한국풍"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=korean%20nine-tailed%20fox%20gumiho%20webtoon%20modern%20seoul%20traditional%20korean%20mythology%20beautiful%20fox%20spirit%20romantic%20fantasy%20professional%20manhwa%20art%20style&width=600&height=400&seq=koreanwebtoon001&orientation=landscape"
              },
              {
                title: "미슐랭 파이브스타 도전기",
                description: "평범한 회사원에서 요리 대회 우승자로. 열정과 노력으로 꿈을 이루는 성장 스토리.",
                tags: ["요리", "성장", "드라마"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=cooking%20competition%20webtoon%20chef%20preparing%20dish%20professional%20kitchen%20passionate%20cooking%20scene%20korean%20manhwa%20style%20inspiring%20story&width=600&height=400&seq=newwebtoon003&orientation=landscape"
              },
              {
                title: "The Haunted Mansion",
                description: "저주받은 저택에 갇힌 사람들의 생존기. 미스터리와 공포가 교차하는 심리 스릴러.",
                tags: ["공포", "스릴러", "미스터리"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=haunted%20mansion%20horror%20webtoon%20dark%20gothic%20house%20trapped%20people%20scary%20atmosphere%20psychological%20thriller%20professional%20digital%20art%20intense%20fear&width=600&height=400&seq=horrorwebtoon001&orientation=landscape"
              },
              {
                title: "검황무적",
                description: "무림 최강자로 거듭나는 소년의 성장기. 정통 무협의 매력과 화려한 검술 액션.",
                tags: ["무협", "액션", "성장"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=martial%20arts%20master%20webtoon%20sword%20emperor%20young%20warrior%20training%20traditional%20chinese%20wuxia%20mountains%20temples%20professional%20manhwa%20art%20epic%20action&width=600&height=400&seq=wuxiawebtoon001&orientation=landscape"
              },
              {
                title: "Digital Dreams",
                description: "가상현실 게임 속에 갇힌 플레이어들. 현실과 가상의 경계가 무너지는 스릴러.",
                tags: ["SF", "스릴러", "게임"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=virtual%20reality%20game%20webtoon%20digital%20world%20trapped%20players%20cyber%20space%20professional%20korean%20manhwa%20art%20style%20futuristic%20technology&width=600&height=400&seq=newwebtoon006&orientation=landscape"
              },
              {
                title: "밤의 속삭임",
                description: "귀신을 보는 능력을 가진 주인공이 겪는 오싹한 일상. 실화 기반 공포 에피소드 시리즈.",
                tags: ["공포", "초자연", "에피소드"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=ghost%20story%20webtoon%20night%20whispers%20supernatural%20horror%20seeing%20spirits%20dark%20atmosphere%20creepy%20encounters%20professional%20korean%20manhwa%20art%20scary&width=600&height=400&seq=horrorwebtoon002&orientation=landscape"
              },
              {
                title: "좀비 아포칼립스",
                description: "좀비로 가득한 세상에서 살아남기 위한 생존 드라마. 인간성과 희망을 지키는 사람들의 이야기.",
                tags: ["좀비", "생존", "액션"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=zombie%20apocalypse%20webtoon%20survival%20drama%20post-apocalyptic%20world%20survivors%20fighting%20undead%20professional%20korean%20manhwa%20art%20style%20intense%20action&width=600&height=400&seq=zombiewebtoon001&orientation=landscape"
              },
              {
                title: "시간을 파는 상점",
                description: "시간을 사고팔 수 있는 신비한 가게. 시간의 가치를 되돌아보게 하는 감동 드라마.",
                tags: ["판타지", "드라마", "감성"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=mysterious%20time%20shop%20webtoon%20magical%20store%20selling%20time%20emotional%20drama%20beautiful%20art%20style%20professional%20korean%20manhwa%20atmospheric%20lighting&width=600&height=400&seq=newwebtoon009&orientation=landscape"
              }
            ].map((webtoon, index) => (
              <article key={index} className="group cursor-pointer animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="mb-4 overflow-hidden rounded-2xl bg-gray-100">
                  <img 
                    alt={webtoon.title}
                    className="w-full h-[200px] sm:h-[240px] object-cover transition-transform duration-700 group-hover:scale-110"
                    src={webtoon.image}
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-block px-2.5 py-1 bg-teal-50 text-teal-700 text-xs rounded-full">웹툰</span>
                  <span className="text-xs text-gray-500">{webtoon.year}</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-teal-600 transition-colors">{webtoon.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">{webtoon.description}</p>
                <div className="flex flex-wrap gap-2">
                  {webtoon.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="px-2.5 py-1 bg-gray-50 text-gray-600 text-xs rounded-full">{tag}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="relative py-16 sm:py-24 lg:py-32 bg-gray-900 overflow-hidden snap-start snap-always min-h-screen flex items-center">
        {/* Animated Background - 더 잘 보이게 opacity 증가 */}
        <div className="absolute inset-0 opacity-15">
          <div 
            className="absolute top-20 left-1/3 w-80 h-80 bg-cyan-400 rounded-full blur-3xl"
            style={{ animation: 'float 8s ease-in-out infinite' }}
          ></div>
          <div 
            className="absolute bottom-10 right-1/3 w-72 h-72 bg-teal-400 rounded-full blur-3xl"
            style={{ animation: 'float 10s ease-in-out infinite reverse' }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12 sm:mb-20 animate-on-scroll">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white font-serif italic">Reviews</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-400 px-4">우리와 함께하는 파트너의 긍정적인 리뷰를 확인하세요</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                review: "라온테크 협약 작가와의 기억은 새로웠습니다. 그 동안 일했던 대부분의 소싱 회사들은 작품 리테이가 늘어나면 항상 비용을 요구하기 바빴습니다. 그런데 라온테크는 더 많은 아이디어를 제공하고 진심으로 잘 되길 바랬습니다.",
                author: "김OO 대표",
                company: "게임 개발사"
              },
              {
                review: "25년 상반기 쯤인것 같습니다.. 우리 회사의 레벨 밸런싱 시스템 구축을 위해 라온테크에 개발을 의뢰했습니다. 견적도 만족 스러웠지만 그 결과물은 지금까지도 사용하고 있습니다.",
                author: "박OO 팀장",
                company: "모바일 게임사"
              },
              {
                review: "라온테크는 오래 전부터 맞춤형 작가 섭취에 독보적인 기술을 가지고 있다고 소문이 나 있었습니다. 다양한 장르의 그래픽소스 제공 서비스 경험, 엔진 연동형 소스, 안정화 된 시스템은 우리의 기반이 되었습니다.",
                author: "이OO CTO",
                company: "웹툰 플랫폼"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-black p-6 sm:p-8 lg:p-10 rounded-3xl border border-gray-800 hover:border-cyan-500/50 transition-all duration-500 hover:scale-105 animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="flex gap-1 mb-4 sm:mb-6">
                  {[...Array(5)].map((_, starIndex) => (
                    <i key={starIndex} className="ri-star-fill text-xl sm:text-2xl text-cyan-400"></i>
                  ))}
                </div>
                <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4 sm:mb-6">
                  "{testimonial.review}"
                </p>
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="text-sm sm:text-base font-semibold text-white">{testimonial.author}</p>
                    <p className="text-xs sm:text-sm text-gray-500">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats-section" className="relative py-16 sm:py-24 lg:py-32 bg-black text-white border-t border-gray-900 overflow-hidden snap-start snap-always min-h-screen flex items-center">
        {/* Animated Background - 더 잘 보이게 opacity 증가 */}
        <div className="absolute inset-0 opacity-15">
          <div 
            className="absolute top-1/3 right-10 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"
            style={{ animation: 'float 11s ease-in-out infinite' }}
          ></div>
          <div 
            className="absolute bottom-1/3 left-20 w-80 h-80 bg-teal-400 rounded-full blur-3xl"
            style={{ animation: 'float 13s ease-in-out infinite reverse' }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 text-center">
            <div className="animate-on-scroll">
              <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 sm:mb-4 font-serif italic bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent">
                {stats.artists}+
              </div>
              <p className="text-gray-400 text-sm sm:text-base lg:text-lg">글로벌 작가 네트워크</p>
            </div>
            <div className="animate-on-scroll" style={{ animationDelay: '0.1s' }}>
              <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 sm:mb-4 font-serif italic bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent">
                {stats.domestic}+
              </div>
              <p className="text-gray-400 text-sm sm:text-base lg:text-lg">국내 베테랑 작가</p>
            </div>
            <div className="animate-on-scroll" style={{ animationDelay: '0.2s' }}>
              <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 sm:mb-4 font-serif italic bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent whitespace-nowrap">
                {stats.year}
              </div>
              <p className="text-gray-400 text-sm sm:text-base lg:text-lg">설립 연도</p>
            </div>
            <div className="animate-on-scroll" style={{ animationDelay: '0.3s' }}>
              <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 sm:mb-4 font-serif italic bg-gradient-to-r from-cyan-400 to-teal-500 bg-clip-text text-transparent">
                Global
              </div>
              <p className="text-gray-400 text-sm sm:text-base lg:text-lg">미국·유럽·아시아 협업</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-24 lg:py-32 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden snap-start snap-always min-h-screen flex items-center">
        {/* Animated Background - 더 잘 보이게 opacity 증가 */}
        <div className="absolute inset-0 opacity-15">
          <div 
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"
            style={{ animation: 'float 10s ease-in-out infinite' }}
          ></div>
          <div 
            className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-teal-400 rounded-full blur-3xl"
            style={{ animation: 'float 12s ease-in-out infinite reverse' }}
          ></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-on-scroll relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 sm:mb-8 text-white font-serif italic">
            함께 성장할<br />파트너를 찾습니다
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 mb-8 sm:mb-12 leading-relaxed px-4">
            라온테크와 함께 새로운 도전을 시작하세요
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-cyan-500 to-teal-600 text-white px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 snap-start snap-always min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid sm:grid-cols-2 gap-10 sm:gap-12 lg:gap-20 mb-12 sm:mb-16">
            <div className="animate-slide-left">
              <h3 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-white">RAON TECH</h3>
              <p className="text-xs sm:text-sm text-cyan-50 leading-relaxed">
                2023년부터 게임소싱 개발을 시작하여<br />
                오로지 게임&웹툰 소스 개발이라는 한 길만 걸어온 글로벌 컴퍼니 빌더
              </p>
            </div>
            <div className="animate-slide-right">
              <h5 className="text-sm sm:text-base font-semibold mb-4 sm:mb-6 text-white">회사 정보</h5>
              <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-cyan-50">
                <p>대표자: 김정헌</p>
                <p>사업자등록번호: 125-51-00975</p>
                <p>대표 연락처: 010-7410-3525</p>
                <p className="pt-2 sm:pt-3">
                  서울특별시 동대문구 장한로14길 81, 105동 704호<br />
                  (장안동, 래미안 장안아파트)
                </p>
              </div>
            </div>
          </div>
          <div className="pt-6 sm:pt-8 border-t border-cyan-400/30 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs sm:text-sm text-cyan-50">
            <p className="text-center sm:text-left">© 2024 RAON TECH Studio. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-cyan-50 opacity-0 hover:opacity-0">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
